import React, {useState} from 'react';
import {FormattedPhoneNumber, getFormattedPhoneNumber} from "../../Common/FormattedPhoneNumber";
import {Collapse} from "react-bootstrap";
import HoursHelper from "../../../services/Menu/services/Availability/HoursHelper";
import {RootState} from "../../../../reducers";
import {selectLocale} from "../../../services/Session/reducer";
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as ShareIco} from "../../../../images/icons/share.svg";
import {ReactComponent as PinOutlineIco} from "../../../../images/icons/pin-outline.svg";
import {ReactComponent as EnvelopeIco} from "../../../../images/icons/email.svg";
import {ReactComponent as ClockIco} from "../../../../images/icons/clock.svg";
import {ReactComponent as PhoneIco} from "../../../../images/icons/phone.svg";
import {ConfigApi, ConfigHoursApi} from "../../../services/Api/types";
import DateService from "../../../services/Common/Date/DateService";
import {getDayOfWeek, isTimeAllDay} from "../../../services/Common/Date/DateHelper";
import {ReactComponent as ArrowBottomIco} from "../../../../images/icons/arrow-bottom.svg";
import RoundedIcon from "../../Common/RoundedIcon";
import TodayOpeningHours from "../../OpeningHours/TodayOpeningHours";

interface OwnProps {
    addressFullString: string;
    config: ConfigApi
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const DrawerStoreSection = ({addressFullString, config, locale}: Props) => {
    const {t} = useTranslation();
    const [collapseOpenHours, setCollapseOpenHours] = useState(false);
    const openHours = config ? HoursHelper.getOpenHours(config.hours, DateService.now()) : undefined;
    const groupedHours = HoursHelper.getOpenHoursGroupedByWeekday(config.hours || []);

    const renderOpenHoursItem = (day: string, hours: ConfigHoursApi[]) => {
        if (!hours || hours.length === 0) return null;

        const mergedHours = HoursHelper.getMergedHourRanges(hours);
        let openDayString = getDayOfWeek(day, locale, undefined, true);

        if (mergedHours.some(h => h.day_to && h.day_to !== h.day_from)) {
            const dayTo = mergedHours.find(h => h.day_to && h.day_to !== h.day_from)?.day_to!;
            openDayString += " - " + getDayOfWeek(dayTo, locale, undefined, true);
        }

        return (
            <>
                <td className="pe-2 align-top">
                    <p className="mb-0">
                        <strong>
                            {`${openDayString}:`}
                        </strong>
                    </p>
                </td>
                <td>
                    {mergedHours.map((h, index) => (
                        <p key={index} className="mb-0">
                            {isTimeAllDay(h.hour_from) && isTimeAllDay(h.hour_to)
                                ? t("common.word.whole_day")
                                : `${h.hour_from} - ${h.hour_to}`}
                        </p>
                    ))}
                </td>
            </>
        );
    };

    return (
        <ul className="navbar-nav justify-content-end flex-grow-1">
            <h5>{t("modules.navbar.field.restaurant.title")}</h5>
            {addressFullString && (
                <li className={"nav-item"}>
                    <a className="nav-link with-icon" href={`https://maps.google.com/?q=${addressFullString}`}>
                        <RoundedIcon icon={<PinOutlineIco/>} size="medium"/>
                        <div className={"nav-link-multiline"}>
                            {addressFullString}
                        </div>
                        <ShareIco/>
                    </a>
                </li>
            )}
            {config?.contact.phone && (
                <li className={"nav-item"}>
                    <a className="nav-link with-icon" href={`tel:${getFormattedPhoneNumber("", config.contact.phone)}`}>
                        <RoundedIcon icon={<PhoneIco/>} size="medium"/>
                        <div className={"nav-link-multiline"}>
                            <FormattedPhoneNumber countryCode="PL" phoneNumber={config.contact.phone}/>
                        </div>
                        <ShareIco/>
                    </a>
                </li>
            )}
            {config?.contact.email && (
                <li className={"nav-item"}>
                    <a className="nav-link with-icon" href={`tel:${config.contact.email}`}>
                        <RoundedIcon icon={<EnvelopeIco/>} size="medium"/>
                        <div className={"nav-link-multiline"}>
                            {config.contact.email}
                        </div>
                        <ShareIco/>
                    </a>
                </li>
            )}
            <li className={"nav-item"}>
                <button className="nav-link with-icon"
                        aria-expanded={collapseOpenHours}
                        aria-controls="collapse_footer_hours"
                        onClick={() => setCollapseOpenHours(!collapseOpenHours)}
                >
                    <RoundedIcon icon={<ClockIco/>} size="medium"/>
                    <div className={"nav-link-multiline"}>
                        {t("common.word.today")}
                        <small>
                            <TodayOpeningHours config={config} openHours={openHours}/>
                        </small>
                    </div>
                    <ArrowBottomIco/>
                </button>
                <Collapse in={collapseOpenHours}>
                    <div id="collapse_footer_hours" className="open-hours">
                        <table>
                            <tbody>
                            {Object.entries(groupedHours).map(([day, hours]) => (
                                <tr key={day}>{renderOpenHoursItem(day, hours)}</tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </Collapse>
            </li>
        </ul>
    )
}

const mapStateToProps = (state: RootState) => ({
    locale: selectLocale(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(DrawerStoreSection));